import { AxTabbedBase } from '@theme/ax-tabbed-base';

/**
 * @typedef {Object} AxTechSpecsTabsRefs
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLElement} [panelsContainer]
 * @property {HTMLElement} [tablist]
 */

/**
 * Tech specs PDP section with two tabs: "What's included" and "Specifications".
 *
 * Both panels are rendered SSR at load — no Section Rendering API fetch needed
 * (panels are lightweight and benefit from instant tab switching).
 *
 * Lives inside <main>, so it's in the model-picker morph zone. After a variant
 * change, the DOM is re-rendered with tab 0 active — we reset our internal state
 * to match. See .claude/rules/pdp-morph.md.
 *
 * @extends {AxTabbedBase}
 */
class AxTechSpecsTabs extends AxTabbedBase {
  static bemPrefix = 'ax-tech-specs-tabs';

  async connectedCallback() {
    await super.connectedCallback();
    document.addEventListener('model:update', this.#onModelUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('model:update', this.#onModelUpdate);
  }

  /**
   * After a model-picker morph, the new DOM has been rendered with tab 0 active.
   * Reset internal state so our cached `#activeIndex` stays in sync — otherwise
   * clicks on the user's previous tab would early-return as "already active".
   *
   * Wait one microtask: the Component MutationObserver updates `this.refs` async,
   * so let it settle before we measure the new tab for the indicator.
   */
  #onModelUpdate = () => {
    queueMicrotask(() => this.resetActiveTab(0));
  };
}

if (!customElements.get('ax-tech-specs-tabs-component')) {
  customElements.define('ax-tech-specs-tabs-component', AxTechSpecsTabs);
}
